import Phaser from 'phaser'
import { GAME_WIDTH, GAME_HEIGHT } from '../../config'
import { BaseMiniGameScene, type MiniGameConfig } from './BaseMiniGameScene'
import { createParticleBurst } from '../../utils/feedback'

/**
 * Test mini-game to verify the base class works correctly
 * Simple tap-the-targets game
 */
export class TestMiniGameScene extends BaseMiniGameScene {
  private targets: Phaser.GameObjects.Arc[] = []
  private spawnTimer?: Phaser.Time.TimerEvent

  constructor() {
    super({ key: 'TestMiniGameScene' })
  }

  getConfig(): MiniGameConfig {
    return {
      maxScore: 1000,
      duration: 30, // 30 second game
      totalItems: 10, // 10 targets to hit
    }
  }

  getNextScene(): string | undefined {
    return undefined // Go to menu after completion
  }

  setupGame(): void {
    // Instructions
    const instructions = this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2 - 50,
      'Tap the circles!\n(Test Mini-Game)',
      {
        fontFamily: 'system-ui',
        fontSize: '24px',
        color: '#ffffff',
        align: 'center',
      }
    )
    instructions.setOrigin(0.5)

    // Fade out instructions after a moment
    this.tweens.add({
      targets: instructions,
      alpha: 0,
      delay: 2000,
      duration: 500,
    })

    // Start spawning targets
    this.spawnTarget()
    this.spawnTimer = this.time.addEvent({
      delay: 2000,
      callback: this.spawnTarget,
      callbackScope: this,
      loop: true,
    })
  }

  private spawnTarget(): void {
    if (this.isComplete) return

    // Random position (avoid edges and UI)
    const x = Phaser.Math.Between(100, GAME_WIDTH - 100)
    const y = Phaser.Math.Between(120, GAME_HEIGHT - 100)

    // Create target circle
    const target = this.add.circle(x, y, 40, 0xff6b6b)
    target.setStrokeStyle(4, 0xffffff)
    target.setInteractive({ useHandCursor: true })

    // Entrance animation
    target.setScale(0)
    this.tweens.add({
      targets: target,
      scale: 1,
      duration: 200,
      ease: 'Back.easeOut',
    })

    // Shrink over time (miss if not tapped)
    this.tweens.add({
      targets: target,
      scale: 0,
      duration: 3000,
      delay: 200,
      ease: 'Linear',
      onComplete: () => {
        if (target.active) {
          this.removeTarget(target, false)
        }
      },
    })

    // Handle tap
    target.on('pointerdown', () => {
      this.hitTarget(target)
    })

    this.targets.push(target)
  }

  private hitTarget(target: Phaser.GameObjects.Arc): void {
    const x = target.x
    const y = target.y

    // Calculate points based on remaining size (faster = more points)
    const sizeRatio = target.scale
    let points: number
    let feedback: string

    if (sizeRatio > 0.7) {
      points = 100
      feedback = 'Perfect!'
      createParticleBurst(this, x, y, 0xffd700, 15)
    } else if (sizeRatio > 0.4) {
      points = 75
      feedback = 'Great!'
      createParticleBurst(this, x, y, 0x4caf50, 10)
    } else {
      points = 50
      feedback = 'Good'
    }

    this.addScore(points, x, y - 30)
    this.showFeedback(feedback, x, y - 60, points === 100 ? 0xffd700 : 0x4caf50)
    this.incrementProgress()

    this.removeTarget(target, true)
  }

  private removeTarget(target: Phaser.GameObjects.Arc, wasHit: boolean): void {
    // Stop any running tweens
    this.tweens.killTweensOf(target)

    // Remove from array
    const index = this.targets.indexOf(target)
    if (index > -1) {
      this.targets.splice(index, 1)
    }

    // Animate out
    if (wasHit) {
      // Pop effect
      this.tweens.add({
        targets: target,
        scale: 1.5,
        alpha: 0,
        duration: 150,
        onComplete: () => target.destroy(),
      })
    } else {
      // Fade out (missed)
      target.destroy()
    }
  }

  // Override to stop spawning when game ends
  protected onTimeUp(): void {
    this.spawnTimer?.destroy()
    super.onTimeUp()
  }

  protected onAllItemsComplete(): void {
    this.spawnTimer?.destroy()
    super.onAllItemsComplete()
  }
}
